// Hot zone map: the half court split into 11 shooting zones, each colored by the player's FG% there
// compared to a typical high school FG% for that kind of shot. Coordinates match ShotChart (feet, 50 x 47).

// Typical high school FG% by shot type (rough varsity averages).
export const HS_AVG = { rim: 55, paint: 40, midrange: 35, corner3: 33, wing3: 30, top3: 30 };
const MIN_SAMPLE = 3;

const NAMES = {
  rim: "Rim", paint: "Paint", mid_l: "Left mid-range", mid_c: "Center mid-range", mid_r: "Right mid-range",
  corner3_l: "Left corner 3", corner3_r: "Right corner 3", wing3_l: "Left wing 3", wing3_r: "Right wing 3", top3: "Top 3",
};
const BASE = (id) => (id.startsWith("mid") ? "midrange" : id.replace(/_[lr]$/, ""));

export const regionOf = (s) => {
  const z = s.zone ?? "midrange";
  if (z === "midrange") return s.x < 19 ? "mid_l" : s.x > 31 ? "mid_r" : "mid_c";
  if (z === "corner3" || z === "wing3") return `${z}_${s.x < 25 ? "l" : "r"}`;
  return z;
};

export const zoneStats = (shots) => {
  const out = Object.fromEntries(Object.keys(NAMES).map((id) => [id, { id, name: NAMES[id], m: 0, a: 0, avg: HS_AVG[BASE(id)] }]));
  shots.forEach((s) => { const r = out[regionOf(s)]; r.a += 1; r.m += s.made ? 1 : 0; });
  Object.values(out).forEach((r) => { r.pct = r.a ? (100 * r.m) / r.a : null; r.diff = r.a ? r.pct - r.avg : null; });
  return out;
};

// Cold (blue) to hot (lime), by points above or below the high school average.
const SCALE = [
  { min: 8, fill: "#39FF14", ink: "#0A0E1A", label: "Hot +8" },
  { min: 3, fill: "#16A34A", ink: "#FFFFFF", label: "Warm +3" },
  { min: -3, fill: "#475569", ink: "#FFFFFF", label: "Average" },
  { min: -8, fill: "#38BDF8", ink: "#0A0E1A", label: "Cool -3" },
  { min: -Infinity, fill: "#1D4ED8", ink: "#FFFFFF", label: "Cold -8" },
];
const tone = (r) => (r.a ? SCALE.find((t) => r.diff >= t.min) : { fill: "#1E293B", ink: "#94A3B8" });

// Shapes in court units (10 per foot). Later shapes cover earlier ones, which carves out the paint and rim.
const LAYERS = [
  { clip: "hz-out", shapes: [["wing3_l", "rect", { x: 0, y: 0, width: 160, height: 470 }], ["top3", "rect", { x: 160, y: 0, width: 180, height: 470 }],
    ["wing3_r", "rect", { x: 340, y: 0, width: 160, height: 470 }], ["corner3_l", "rect", { x: 0, y: 0, width: 80, height: 140 }],
    ["corner3_r", "rect", { x: 420, y: 0, width: 80, height: 140 }]] },
  { clip: "hz-in", shapes: [["mid_l", "rect", { x: 0, y: 0, width: 190, height: 470 }], ["mid_c", "rect", { x: 190, y: 0, width: 120, height: 470 }],
    ["mid_r", "rect", { x: 310, y: 0, width: 190, height: 470 }]] },
  { shapes: [["paint", "rect", { x: 190, y: 0, width: 120, height: 190 }], ["rim", "circle", { cx: 250, cy: 52.5, r: 40 }]] },
];
const LABEL_AT = { rim: [250, 60], paint: [250, 140], mid_l: [110, 150], mid_c: [250, 225], mid_r: [390, 150], corner3_l: [40, 80],
  corner3_r: [460, 80], wing3_l: [70, 320], wing3_r: [430, 320], top3: [250, 340] };
const ARC = "M52.5 0 L52.5 52.5 A197.5 197.5 0 0 0 447.5 52.5 L447.5 0 Z";

const ZoneLabel = ({ r }) => {
  const [x, y] = LABEL_AT[r.id];
  const t = tone(r);
  const small = r.id.startsWith("corner");
  return (
    <g data-testid={`hz-label-${r.id}`} fill={t.ink} textAnchor="middle" className="pointer-events-none font-mono-gd" opacity={r.a && r.a < MIN_SAMPLE ? 0.75 : 1}>
      <text x={x} y={y} fontSize={small ? 17 : 24} fontWeight="700">{r.a ? `${Math.round(r.pct)}%` : "—"}</text>
      <text x={x} y={y + (small ? 19 : 24)} fontSize={small ? 13 : 16}>{`${r.m}/${r.a}`}</text>
    </g>
  );
};

export const HotZones = ({ shots, lines }) => {
  const stats = zoneStats(shots);
  return (
    <div className="mx-auto w-full max-w-xl space-y-3">
      <svg data-testid="hot-zone-court" viewBox="0 0 500 470" className="block w-full rounded-2xl bg-[#0F172A]">
        <defs>
          <clipPath id="hz-in"><path d={ARC} /></clipPath>
          <clipPath id="hz-out"><path d={`M0 0 H500 V470 H0 Z ${ARC}`} clipRule="evenodd" /></clipPath>
        </defs>
        {LAYERS.map((layer, i) => (
          <g key={i} clipPath={layer.clip ? `url(#${layer.clip})` : undefined}>
            {layer.shapes.map(([id, Tag, props]) => (
              <Tag key={id} data-testid={`hz-zone-${id}`} {...props} fill={tone(stats[id]).fill} stroke="#0A0E1A" strokeWidth="3">
                <title>{`${NAMES[id]}: ${stats[id].m}/${stats[id].a}${stats[id].a ? ` (${Math.round(stats[id].pct)}%, HS avg ${stats[id].avg}%)` : ""}`}</title>
              </Tag>
            ))}
          </g>
        ))}
        <g opacity="0.35" className="pointer-events-none">{lines}</g>
        {Object.values(stats).map((r) => <ZoneLabel key={r.id} r={r} />)}
      </svg>
      <div data-testid="hot-zone-legend" className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono-gd text-[11px] uppercase tracking-wider text-slate-300">
        {SCALE.map((t) => <span key={t.label} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: t.fill }} />{t.label}</span>)}
        <span className="normal-case tracking-normal text-slate-400">Points vs typical high school FG% (rim {HS_AVG.rim}, paint {HS_AVG.paint}, mid {HS_AVG.midrange}, corner 3 {HS_AVG.corner3}, other 3s {HS_AVG.wing3}). Faded numbers: fewer than {MIN_SAMPLE} shots.</span>
      </div>
    </div>
  );
};
